import { useState } from "react";
import { useTrackedImagePreload } from "../hooks/useTrackedImagePreload";
import logoBlueLong from "../assets/brand/logos/AzulOscuroLargo.png";
import logoBlueMedium from "../assets/brand/logos/AzulOscuroMedio.webp";
import logoBlueShort from "../assets/brand/logos/AzulOscuroCorto.png";
import logoWhiteLong from "../assets/brand/penca/LogoPencaBlanco.webp";
import pelota from "../assets/brand/penca/pelota.webp";
import loginBackground from "../assets/brand/backgrounds/DG2.webp";
import loginBackgroundMobile from "../assets/brand/backgrounds/DG2Mobile.webp";
import { SiteFooter } from "../components/SiteFooter";
import { LoginForm } from "../components/auth/LoginForm";
import { TrackedImage } from "../components/ui/TrackedImage";

const FAQ_ITEMS = [
  {
    id: "how-participate",
    question: "¿Cómo participo?",
    answer: [
      "Para participar tenés que registrarte aquí y completar tus predicciones para los partidos del torneo.",
    ],
  },
  {
    id: "deadline-results",
    question: "¿Hasta cuándo puedo cargar mis resultados?",
    answer: [
      "Las predicciones se pueden cargar o modificar hasta el inicio de cada partido.",
      "Una vez comenzado, el resultado queda bloqueado.",
    ],
  },
  {
    id: "points",
    question: "¿Cómo se calculan los puntos?",
    answer: [
      "Si acertás el resultado exacto del partido, sumás 8 puntos.",
      "Si acertás el ganador y la diferencia de gol, sumás 5 puntos.",
      "Si acertás únicamente el ganador o el empate, sumás 3 puntos.",
      "Si no acertás el resultado, no sumás puntos.",
    ],
  },
  {
    id: "extra-points",
    question: "¿Hay puntos extra por asistir a la tnuá?",
    answer: [
      "Sí. Los janijim que asistan a la tnuá durante los sábados en los que se juegue el torneo podrán escanear un código QR que estará disponible en cada tnuá.",
      "Al validarse la asistencia, se sumarán 2 puntos extra por cada sábado asistido.",
    ],
  },
  {
    id: "result-counts",
    question: "¿Qué resultado se toma en cuenta?",
    answer: [
      "Se toma el resultado al finalizar los 90 minutos reglamentarios más el tiempo agregado.",
      "El alargue y los penales no cuentan para la penca.",
    ],
  },
  {
    id: "penalties",
    question: "¿Qué pasa en los partidos que van a penales?",
    answer: [
      "Aunque un equipo clasifique por penales, para la penca solo se considera el resultado de los 90 minutos reglamentarios.",
    ],
  },
  {
    id: "change-predictions",
    question: "¿Puedo cambiar mis predicciones?",
    answer: [
      "Sí. Podés modificar tus resultados hasta el comienzo de cada partido.",
    ],
  },
  {
    id: "ranking",
    question: "¿Dónde veo la tabla de posiciones?",
    answer: [
      "La tabla general estará disponible dentro de la plataforma y se actualizará automáticamente después de cada partido.",
    ],
  },
  {
    id: "tie-break",
    question: "¿Qué pasa si dos personas terminan con el mismo puntaje?",
    answer: [
      "En caso de empate en puntos, la posición se definirá según la cantidad de resultados exactos acertados.",
    ],
  },
  {
    id: "cost",
    question: "¿La participación tiene costo?",
    answer: [
      "No. La participación en la penca es totalmente gratuita para toda la comunidad.",
    ],
  },
  {
    id: "organizer",
    question: "¿Quién organiza la penca?",
    answer: [
      "La penca es organizada por la Organización Sionista del Uruguay para toda la comunidad.",
    ],
  },
];

export function LoginPage() {
  useTrackedImagePreload(loginBackground);
  useTrackedImagePreload(loginBackgroundMobile);
  const faqLeft = FAQ_ITEMS.filter((_, index) => index % 2 === 0);
  const faqRight = FAQ_ITEMS.filter((_, index) => index % 2 === 1);
  const [openLeftFaqId, setOpenLeftFaqId] = useState("extra-points");
  const [openRightFaqId, setOpenRightFaqId] = useState("");

  return (
    <div
      className="min-h-screen overflow-x-hidden bg-brand-surface text-brand-primary"
    >
      <main className="flex min-h-screen w-full flex-col">
        <section
          className="auth-background flex min-h-screen flex-col"
          style={{
            "--auth-background-image": `url(${loginBackground})`,
            "--auth-background-mobile-image": `url(${loginBackgroundMobile})`,
          }}
        >
          <header className="flex items-start justify-start px-[40px] pt-[40px] sm:justify-between sm:px-[44px] sm:pt-[44px]">
            <TrackedImage
              src={logoBlueLong}
              alt="Penca OSU"
              className="h-[52px] w-auto object-contain sm:hidden"
            />
            <TrackedImage
              src={logoBlueMedium}
              alt="Penca OSU"
              className="hidden h-[52px] w-auto object-contain sm:block sm:h-[72px]"
            />
            <TrackedImage
              src={logoBlueShort}
              alt="Penca OSU"
              className="hidden h-[52px] w-auto object-contain sm:block sm:h-[72px]"
            />
          </header>

          <div className="flex flex-1 items-center justify-center px-4 py-6 sm:px-6 sm:py-10 lg:px-8 lg:py-8">
            <div className="grid w-full max-w-6xl items-stretch overflow-hidden rounded-[2.5rem] bg-brand-surface/95 shadow-[0_28px_90px_rgba(20,50,135,0.16)] backdrop-blur lg:grid-cols-2">
              <div className="relative flex min-h-[340px] h-full flex-col justify-between overflow-hidden bg-brand-primary px-6 py-8 text-white sm:px-8 sm:py-10 lg:px-10 lg:py-12">
                <div className="relative mx-auto flex w-full flex-1 items-center justify-center px-4">
                  <TrackedImage
                    src={logoWhiteLong}
                    alt="Penca OSU"
                    className="h-auto w-full max-w-[360px] drop-shadow-[0_10px_30px_rgba(0,0,0,0.22)]"
                  />
                </div>
              </div>

              <div className="h-full bg-brand-surface px-5 py-8 sm:px-8 sm:py-10 lg:px-10 lg:py-12">
                <div className="mx-auto flex h-full w-full max-w-lg flex-col justify-center">
                  <div className="mb-10 text-center">
                    <p className="font-display text-[2rem] font-medium leading-[0.98] text-brand-accent-light sm:text-[2.75rem]">
                      ¡Hola! Iniciá sesión
                      <br />
                      para continuar.
                    </p>
                  </div>

                  <LoginForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="relative z-10 mt-10 bg-brand-surface px-4 py-12 sm:mt-12 sm:px-6 sm:py-16 lg:mt-14 lg:px-8 lg:py-20">
          <div className="mx-auto w-full max-w-7xl">
            <h2 className="text-center font-display text-[1.9rem] leading-none tracking-[-0.03em] text-brand-secondary sm:text-left sm:text-5xl">
              Preguntas frecuentes
            </h2>

            <div className="mt-12 grid gap-6 md:grid-cols-2 md:gap-8">
              <div className="flex flex-col gap-6 md:gap-8">
                {faqLeft.map((item) => (
                  <FaqItem
                    key={item.id}
                    item={item}
                    isOpen={openLeftFaqId === item.id}
                    onToggle={() =>
                      setOpenLeftFaqId((current) =>
                        current === item.id ? "" : item.id
                      )
                    }
                  />
                ))}
              </div>

              <div className="flex flex-col gap-6 md:gap-8">
                {faqRight.map((item) => (
                  <FaqItem
                    key={item.id}
                    item={item}
                    isOpen={openRightFaqId === item.id}
                    onToggle={() =>
                      setOpenRightFaqId((current) =>
                        current === item.id ? "" : item.id
                      )
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <div className="relative z-10">
          <SiteFooter />
        </div>
      </main>
    </div>
  );
}

function FaqItem({ item, isOpen, onToggle }) {
  return (
    <article
      className={`rounded-2xl border border-brand-secondary/70 bg-brand-surface px-5 py-4 transition-shadow sm:px-6 sm:py-5 ${
        isOpen ? "shadow-[0_16px_32px_rgba(20,50,135,0.08)]" : "shadow-none"
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 text-left"
        aria-expanded={isOpen}
        aria-controls={`${item.id}-content`}
      >
        <span className="flex min-w-0 items-center gap-3">
          <TrackedImage
            src={pelota}
            alt=""
            aria-hidden="true"
            className="h-7 w-7 shrink-0 sm:h-8 sm:w-8"
          />
          <span className="text-lg font-extrabold leading-tight text-brand-primary normal-case sm:text-xl">
            {item.question}
          </span>
        </span>

        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-brand-secondary text-brand-secondary">
          <ChevronIcon className={isOpen ? "rotate-180" : ""} />
        </span>
      </button>

      {isOpen ? (
        <div
          id={`${item.id}-content`}
          className="mt-5 pl-10 text-sm leading-6 text-brand-primary sm:pl-11 sm:text-base"
        >
          <div className="grid gap-2">
            {item.answer.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      ) : null}
    </article>
  );
}

function ChevronIcon({ className = "" }) {
  return (
    <svg
      className={`h-4 w-4 transition-transform ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
